import React, { useEffect, useRef, useState, useCallback, memo } from "react";
import {
  AppBar,
  Badge,
  Box,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  styled,
  Toolbar,
  Typography,
  ListItemAvatar,
  Avatar,
  Link,
  Drawer,
  Stack,
  Container,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import Image from "../Image";
import SearchIcon from "@mui/icons-material/Search";
import GarageOutlinedIcon from "@mui/icons-material/GarageOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PhoneIcon from "@mui/icons-material/Phone";
import { useArgusAuth } from "../../hooks/useArgusAuth";
import useArgusSearch from "../../hooks/ArgusSearchHook";
import { SouthAfricanRand } from "../../utils/helpers";
import CustomButton from "../CustomButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import cssVariables from "../../utils/cssVariables";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTheme } from "@mui/system";
import MenuIcon from "@mui/icons-material/Menu";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { useArgusCart } from "../../contexts/ArgusCartContext";
import { ProductAvailability } from "../../utils/constants";
import { useLocation, useNavigate } from "react-router-dom";
import { COLORS } from "../../constants";
import SearchBar from "../SearchBar/SearchBar";
import SiteTopBar from "../SiteTopbar";
import SiteBottombar from "../SiteBottombar";

const DesktopHeader = ({
  genericPageContent,
  gotoLoginPage,
  gotoRegistrationPage,
  gotoCartPage,
  performLogout,
  cartItems,
  totalGarageItems,
  openSavedVehiclesModal,
  categoriesAndTypes,
  firstName,
  lastName,
  accountType,
  tecfinityAccountNumber,
  tecfinityAccountName,
  tecfinityAvailableCredit,
}) => {
  const [accountDropdownOpen, setAccountDropdownOpen] = React.useState(false);
  const [searchDropdownOpen, setSearchDropdownOpen] = React.useState(false);
  const { cartGrandTotal } = useArgusCart();
  const { handleAddToCart } = useArgusCart();
  const { loading, products, setSearchTerm } = useArgusSearch();
  const [searchInput, setSearchInput] = useState("");
  const [categoriesDrawerOpen, setCategoriesDrawerOpen] = useState(false);
  const [typesDrawerOpen, setTypesDrawerOpen] = useState(false);
  const [categoryToDisplay, setCategoryToDisplay] = useState(null);
  const searchInputRef = useRef(null);

  const theme = useTheme();

  const toggleCategories = () => {
    setCategoriesDrawerOpen(!categoriesDrawerOpen);
  };

  const toggleTypes = () => {
    setTypesDrawerOpen(!typesDrawerOpen);
  };

  const handleCategoryClick = (category) => {
    setCategoryToDisplay(category);
    toggleTypes();
  };

  const handleCloseTypesClick = () => {
    setCategoryToDisplay(null);
    toggleTypes();
  };

  const handleAccountDropdownClick = () => {
    setAccountDropdownOpen(!accountDropdownOpen);
  };

  const StyledAppBar = styled(AppBar)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.text.primary,
    boxShadow: "none",
    padding: 0,
    borderBottom: `1px solid ${COLORS.border}`,
  }));

  const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    justifyContent: "space-between",
    padding: "var(--spacing-m) 0",
    // paddingLeft: theme.spacing(1),  // Minimal padding
    // paddingRight: theme.spacing(1),
  }));

  const ContactAndCartContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: "var(--spacing-xl)", // 1.75rem space between contact and cart sections
  }));

  const ContactSection = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: "var(--spacing-xs)", // 0.5rem space between icon and text
  }));

  const CartSection = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: "var(--spacing-m)", // 0.5rem space between icon and text
  }));

  const TextContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
  }));

  useEffect(() => {
    if (!!searchDropdownOpen) {
      setAccountDropdownOpen(false);
    }
  }, [searchDropdownOpen]);

  useEffect(() => {
    if (!!accountDropdownOpen) {
      setSearchDropdownOpen(false);
    }
  }, [accountDropdownOpen]);

  return (
    <>
      <StyledAppBar position="static" sx={{ border: "none" }}>
        <SiteTopBar
          totalGarageItems={totalGarageItems}
          openSavedVehiclesModal={openSavedVehiclesModal}
          gotoLoginPage={gotoLoginPage}
          gotoRegistrationPage={gotoRegistrationPage}
          gotoCartPage={gotoCartPage}
          performLogout={performLogout}
          tecfinityAccountNumber={tecfinityAccountNumber}
          tecfinityAccountName={tecfinityAccountName}
          tecfinityAvailableCredit={tecfinityAvailableCredit}
          firstName={firstName}
          lastName={lastName}
          accountType={accountType}
        />
        <Container disableGutters>
          <StyledToolbar>
            {/* Hamburger menu button for all categories!!! */}
            {/* <CustomButton
              edge="start"
              onClick={toggleCategories}
              basic
              startIcon={<MenuIcon fontSize="small" />}
              color="#333"
            /> */}
            <Box sx={{ display: "flex", alignItems: "center", flex: 2 }}>
              <Image
                src="https://argusweb.azureedge.net/argus-web/logo.svg"
                alt="Logo"
                size="xl"
                href="/"
              />

              {window.location.pathname.includes("cart") === false && (
                <SearchBar
                  searchInput={searchInput}
                  setSearchInput={setSearchInput}
                  searchDropdownOpen={searchDropdownOpen}
                  setSearchDropdownOpen={setSearchDropdownOpen}
                  loading={loading}
                  products={products}
                  setSearchTerm={setSearchTerm}
                  handleAddToCart={handleAddToCart}
                  setAccountDropdownOpen={setAccountDropdownOpen}
                  categoriesAndTypes={categoriesAndTypes}
                />
              )}
            </Box>

            <ContactAndCartContainer>
              {/* Contact Section */}
              <ContactSection>
                <PhoneIcon
                  sx={{
                    fontSize: "var(--font-size-xxl)",
                    color: "var(--text-color)",
                  }}
                />
                <TextContainer>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "var(--font-size-xxs)",
                      color: COLORS.lightText,
                      fontWeight: 300,
                      fontFamily: "Rubik",
                    }}
                  >
                    Call us:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "var(--font-size-xs)", // 0.875rem
                      color: COLORS.darkText,
                      fontWeight: 600, // Rubik SemiBold
                      fontFamily: "Rubik",
                    }}
                  >
                    +27 (0) 11 747 5200
                  </Typography>
                </TextContainer>
              </ContactSection>

              {/* Cart Section */}
              <CartSection>
                <IconButton edge="end" onClick={gotoCartPage}>
                  <Badge
                    badgeContent={cartItems}
                    sx={{
                      "& .MuiBadge-badge": {
                        backgroundColor: COLORS.themeYellow,
                        color: COLORS.darkText,
                        fontWeight: 600,
                      },
                    }}
                  >
                    <ShoppingCartIcon
                      sx={{
                        fontSize: "var(--font-size-xxl)",
                        color: "var(--text-color)",
                      }}
                    />
                  </Badge>
                </IconButton>
                <TextContainer>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "var(--font-size-xxs)", // 0.75rem
                      color: COLORS.lightText,
                      fontWeight: 300, // Rubik Regular
                      fontFamily: "Rubik",
                    }}
                  >
                    Shopping cart:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "var(--font-size-xs)", // 0.875rem
                      color: COLORS.darkText,
                      fontWeight: 600, // Rubik SemiBold
                      fontFamily: "Rubik",
                    }}
                  >
                    {SouthAfricanRand.format(cartGrandTotal)}
                  </Typography>
                </TextContainer>
              </CartSection>
            </ContactAndCartContainer>
          </StyledToolbar>
        </Container>

        <SiteBottombar
          categoriesAndTypes={categoriesAndTypes}
          genericPageContent={genericPageContent}
        />
      </StyledAppBar>
    </>
  );
};

export default DesktopHeader;
